import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import * as _ from 'lodash';

import { ModalService } from 'src/app/components/modals/modal.service';

import { StateService, TransitionService } from '@uirouter/core';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { BulkDeleteConfirmationComponent } from '../bulk-delete-confirmation/bulk-delete-confirmation.component';
import {
  BatchActionHandlerService,
  ListOperation,
  PaginatedScrollingListService,
  ScrollingListStateService,
} from '../services';

@Component({
  selector: 'batch-actions',
  templateUrl: './batch-actions.component.html',
  styleUrls: ['./batch-actions.component.scss'],
  standalone: false,
})
export class BatchActionsComponent implements OnInit, OnDestroy {
  @Input() styles: string = 'xl:!tw-min-w-[850px]';

  /** Menu icons: stroke SVGs use `currentColor` (set via text color). */
  readonly bulkMenuIconClass =
    '!tw-mr-4 tw-fill-gray-400 tw-text-gray-400 !tw-w-[16px] !tw-h-[16px] !tw-scale-100';

  readonly bulkMenuIconClassLeading = `${this.bulkMenuIconClass} tw-shrink-0`;

  additionalOperations: any[] = [];
  transitionHook: any;

  constructor(
    private stateService: StateService,
    private transitionService: TransitionService,
    private modalService: ModalService,
    private permissionService: PermissionService,
    protected batchService: BatchActionHandlerService,
    protected listService: PaginatedScrollingListService<any>,
    protected listStateService: ScrollingListStateService<any>,
  ) {}

  ngOnInit(): void {
    this.batchService.operationsChanges().subscribe(() => {
      this._filterByRole();
      this._updateDeleteAction();

      this.additionalOperations = this.batchService.listOperations.operations
        .filter((op: any) => !op.showIn || op.showIn.includes('bulk'))
        .filter((op: any) => !op.mainOperation)
        // Submenu-shaped operations (e.g. Remote Commands) have no `onClick`
        // of their own and are rendered via the dedicated nested-flyout path
        // (see `findRemoteCommandsOperation`), not as flat menu items here.
        .filter((op: any) => !op.submenu);
    });

    let _bypass = false;
    this.transitionHook = this.transitionService.onStart({}, (trans: any) => {
      if (_bypass) {
        _bypass = false;
        return;
      }
      if (this.listService.activeOperation) {
        var operationName =
          this.listService.activeOperation?.name?.toLowerCase();

        trans.abort();

        this.modalService
          .confirm(
            'Cancel bulk ' + operationName + '?',
            'A bulk ' +
              operationName +
              ' is in progress. Navigating away from this page will cancel this action. Are you sure you want to cancel?',
            'Yes, Cancel',
            'No',
          )
          .then(() => {
            this.batchService.cancel();

            _bypass = true;
            // Use transition.params(), not trans.to().params — the latter is the
            // state's Param declarations (objects), which stringify to
            // `[object Object]` in query params like `?open=`.
            this.stateService.go(trans.to().name, trans.params());
          });
      }
    });

    window.onbeforeunload = (e: Event) => {
      if (this.listService.activeOperation) {
        e.preventDefault();
        e.returnValue = true;
      }
    };
  }

  ngOnDestroy(): void {
    window.onbeforeunload = undefined;

    this.transitionHook();
  }

  _filterByRole() {
    _.remove(this.batchService.listOperations.operations, (operation: any) => {
      if (operation.submenu && operation.children) {
        _.remove(operation.children, (child: any) => {
          if (child.separator) {
            return false;
          }
          return !this.meetsUnscopedOperationRequirement(child);
        });
        return (
          !this.meetsUnscopedOperationRequirement(operation) ||
          !operation.children.length
        );
      }
      return !this.meetsUnscopedOperationRequirement(operation);
    });
  }

  private meetsUnscopedOperationRequirement(operation: ListOperation<any>) {
    if (
      operation.requireAccess &&
      operation.requireRole &&
      operation.showIn?.includes('single')
    ) {
      return this.permissionService.meetsAccessRequirement({
        requireRole: operation.requireRole,
      });
    }

    return this.permissionService.meetsAccessRequirement(operation);
  }

  _updateDeleteAction() {
    const wireDelete = (operation: any) => {
      if (operation.separator) {
        return;
      }
      if (operation.isDelete) {
        operation.beforeBatchAction = (selectedItems: any[]) => {
          return this.modalService.showDialog(
            BulkDeleteConfirmationComponent,
            {
              selectedItems: selectedItems,
              itemName: this.listService.activeOperation?.name,
            },
            { width: this.modalService.MODAL_SM },
          );
        };
      }
    };
    _.each(this.batchService.listOperations.operations, (operation: any) => {
      wireDelete(operation);
      if (operation.submenu && operation.children) {
        _.each(operation.children, wireDelete);
      }
    });
  }

  executeOperation(operation: any): void {
    if (operation && operation.onClick) {
      operation.onClick(
        undefined,
        undefined,
        undefined,
        this.batchService.listOperations.getStorageSearchApi?.(),
      );
    }
  }

  toggleSelectAll(): void {
    if (this.listStateService.isAllSelected()) {
      this.listStateService.deselectAll();
    } else {
      this.listStateService.selectAll();
    }
  }

  hasAvailableOperations(): boolean {
    const mainVisible = this.filteredOperations.filter(
      (op) => op.mainOperation,
    );
    const mainOps = mainVisible.some((op) => {
      if (op.submenu && op.children?.length) {
        return this.visibleSubmenuChildren(op).length > 0;
      }
      return true;
    });
    return mainOps || this.filteredAdditionalOperations.length > 0;
  }

  visibleSubmenuChildren(operation: ListOperation<any>): ListOperation<any>[] {
    const selected = this.listStateService.getSelected();
    return (
      operation.children?.filter(
        (c) =>
          c.separator ||
          (!(c.hidden && c.hidden(selected)) && this.isAllowedForSelection(c)),
      ) ?? []
    );
  }

  /** Remote Commands parent op (⋮ submenu), or null if hidden / no visible children. */
  remoteCommandsBulkOperation(): ListOperation<any> | null {
    return this.findRemoteCommandsOperation();
  }

  /** Rows for the nested Remote Commands `mat-menu` (includes `separator` entries for dividers). */
  get remoteCommandsMenuChildren(): ListOperation<any>[] {
    const op = this.findRemoteCommandsOperation();
    return op ? this.visibleSubmenuChildren(op) : [];
  }

  get hasBulkDropdownContent(): boolean {
    return (
      this.filteredAdditionalOperations.length > 0 ||
      this.findRemoteCommandsOperation() != null
    );
  }

  protected get filteredMainBarOperations(): ListOperation<any>[] {
    return this.filteredOperations.filter(
      (op) => op.mainOperation && !op.submenu,
    );
  }

  private findRemoteCommandsOperation(): ListOperation<any> | null {
    const selected = this.listStateService.getSelected();
    // Submenu operations always get the dedicated nested-flyout treatment,
    // regardless of whether they're placed on the bar or in the overflow
    // menu (mainOperation only controls bar-vs-overflow placement).
    const op = this.filteredOperations.find(
      (o) => o.submenu && o.children?.length,
    );
    if (!op || (op.hidden && op.hidden(selected))) {
      return null;
    }
    return this.visibleSubmenuChildren(op).length > 0 ? op : null;
  }

  getOperationIcon(operationName: string): string {
    const iconMap: { [key: string]: string } = {
      Delete: 'trash',
      Move: 'move-to-folder',
      License: 'license',
      'Remote Commands': 'remote-commands',
      'Launch Rise Player': 'play-no-fill',
      'Restart Rise Player': 'restart',
      'Reboot Media Player': 'reboot',
      'Turn On Screen': 'turn-on-screen',
      'Turn Off Screen': 'turn-off-screen',
      'Update Firmware': 'update',
      Export: 'export-all',
      'Export All': 'export-all',
      Copy: 'duplicate',
      'Copy to Sub-company': 'duplicate',
      'Make a Copy': 'duplicate',
      'Screen Sharing': 'screen-share',
      'Set Monitoring': 'monitoring',
      'Set Reboot Time': 'reboot-time',
      'Set Address': 'set-address',
      'Enable Display Control': 'enable-display',
      'Disable Display Control': 'disable-display',
      'Move To Company': 'move-display',
      'Move To Trash': 'trash',
      'Assign Schedule': 'calendar',
      Download: 'download',
      Duplicate: 'duplicate',
      'Copy URL': 'copy',
      Rename: 'edit',
      'Control Alert Volume': 'volume',
    };

    return iconMap[operationName] || 'alert-triangle';
  }

  protected get filteredOperations() {
    return (
      this.batchService.listOperations?.operations
        .filter((op) => !op.showIn || op.showIn.includes('bulk'))
        .filter(
          (op) =>
            !(op.hidden && op.hidden(this.listStateService.getSelected())),
        )
        .filter((op) => this.isAllowedForSelection(op)) || []
    );
  }

  protected get filteredAdditionalOperations() {
    return (
      this.additionalOperations
        .filter(
          (op) =>
            !(op.hidden && op.hidden(this.listStateService.getSelected())),
        )
        .filter((op: any) => !op.showIn || op.showIn.includes('bulk'))
        .filter((op) => this.isAllowedForSelection(op)) || []
    );
  }

  private isAllowedForSelection(operation: ListOperation<any>): boolean {
    if (!operation.requireAccess) {
      return true;
    }

    const selected = this.listStateService
      .getSelected()
      .filter((item) => !item.isFolder);

    if (!selected.length) {
      return false;
    }

    return selected.every((item) =>
      this.permissionService.meetsAccessRequirement({
        ...operation,
        entityId: item.item.id,
      }),
    );
  }
}
